import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, Trophy, Gamepad2, User as UserIcon, Edit2, History as HistoryIcon, Volume2, VolumeX, Music } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import VintageLayout from '../components/layout/VintageLayout';
import { useTheme } from '../context/ThemeContext';
import ActiveGamesList from '../components/dashboard/ActiveGamesList';
import GameHistory from '../components/dashboard/GameHistory';
import Leaderboard from '../components/dashboard/Leaderboard';
import { audioManager } from '../utils/AudioManager';

export const DashboardPage: React.FC = () => {
    const { user, logout } = useAuth();
    const { availableThemes, setTheme: setContextTheme, currentTheme } = useTheme();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'SETTINGS' | 'ATELIER' | 'HISTORY' | 'LEADERBOARD'>('OVERVIEW');
    const [selectedAvatar, setSelectedAvatar] = useState(user?.avatar || 'default_avatar');
    // Initialize with current theme ID from context instead of user pref if possible, or fallback
    const [selectedTheme, setSelectedTheme] = useState(currentTheme?._id || user?.theme || 'default');

    // Atelier State
    const [allPacks, setAllPacks] = useState<{ officials: any[], mine: any[] }>({ officials: [], mine: [] });
    const [selectedPackId, setSelectedPackId] = useState('');
    const [joinRoomCode, setJoinRoomCode] = useState('');
    const [myPacks, setMyPacks] = useState<any[]>([]);
    const [newPackData, setNewPackData] = useState({ name: '', description: '', isAdult: false });
    const [isCreatingPack, setIsCreatingPack] = useState(false);

    // Game Session State
    const [activeGames, setActiveGames] = useState<any[]>([]);
    const [gameHistory, setGameHistory] = useState<any[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [leaderboard, setLeaderboard] = useState<any[]>([]);
    const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

    // Audio State
    const [isMuted, setIsMuted] = useState(audioManager.muted);
    const [musicVolume, setMusicVolume] = useState(audioManager.currentMusicVolume);
    const [sfxVolume, setSfxVolume] = useState(audioManager.currentSFXVolume);

    // Audio Initialization
    React.useEffect(() => {
        audioManager.playMusic('landing');
        return () => {
            // Optional: audioManager.fadeOutMusic();
        };
    }, []);

    // Load Data when tab changes
    React.useEffect(() => {
        if (activeTab === 'HISTORY') {
            fetchHistory();
        } else if (activeTab === 'LEADERBOARD') {
            fetchLeaderboard();
        }
    }, [activeTab]);

    const fetchHistory = async () => {
        try {
            setLoadingHistory(true);
            const { data } = await api.get('/games/history');
            if (data && data.history) {
                setGameHistory(data.history);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
            toast.error("Accès aux archives impossible. Vérifiez votre connexion.");
        } finally {
            setLoadingHistory(false);
        }
    };

    const fetchLeaderboard = async () => {
        try {
            setLoadingLeaderboard(true);
            const { data } = await api.get('/auth/leaderboard');
            if (data && data.leaderboard) {
                setLeaderboard(data.leaderboard);
            }
        } catch (error) {
            console.error('Error fetching leaderboard:', error);
            toast.error("Données de classement indisponibles pour le moment.");
        } finally {
            setLoadingLeaderboard(false);
        }
    };

    const fetchMyPacks = async () => {
        try {
            const { data } = await api.get('/packs');
            if (data) {
                setAllPacks(data);
                if (data.mine) setMyPacks(data.mine);
                if (!selectedPackId && data.officials && data.officials.length > 0) {
                    setSelectedPackId(data.officials[0]._id);
                }
            }
        } catch (error) {
            console.error('Error fetching packs:', error);
        }
    };

    const fetchActiveGames = async () => {
        try {
            // Import sessionManager to get the playerId
            const { sessionManager } = await import('../utils/sessionManager');
            const playerId = sessionManager.getPlayerId();

            const { data } = await api.get('/games/active', {
                headers: {
                    'X-Player-Id': playerId
                }
            });
            setActiveGames(data.activeGames || []);
        } catch (error) {
            console.error('Error fetching active games:', error);
        }
    };

    const fetchGameHistory = async () => {
        try {
            const { data } = await api.get('/games/history');
            setGameHistory(data.history || []);
        } catch (error) {
            console.error('Error fetching game history:', error);
        }
    };

    const handleAbandonGame = async (roomId: string) => {
        try {
            const { sessionManager } = await import('../utils/sessionManager');
            const playerId = sessionManager.getPlayerId();

            await api.post(`/games/${roomId}/abandon`, {}, {
                headers: {
                    'X-Player-Id': playerId
                }
            });
            toast.success('Partie abandonnée');
            fetchActiveGames(); // Refresh list
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Échec de la déconnexion neuronale.');
        }
    };

    React.useEffect(() => {
        if (activeTab === 'ATELIER') {
            fetchMyPacks();
        } else if (activeTab === 'OVERVIEW') {
            fetchActiveGames();
            fetchGameHistory();
            fetchMyPacks(); // Ensure packs are loaded for quick play selection
        }
    }, [activeTab]);

    // Sync local state when context theme changes (e.g. initial load)
    React.useEffect(() => {
        if (currentTheme?._id) {
            setSelectedTheme(currentTheme._id);
        }
    }, [currentTheme]);

    const handleCreatePack = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/packs', newPackData);
            toast.success('Pack créé avec succès !');
            setNewPackData({ name: '', description: '', isAdult: false });
            setIsCreatingPack(false);
            fetchMyPacks();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Échec de l\'initialisation du serveur.');
        }
    };

    // Generated vintage comic style avatars
    const avatars = [
        'avatar_m01_grue_jaune_1769172985788.png',
        'avatar_m02_pont_st_nazaire_1769173001049.png',
        'avatar_m03_tour_eiffel_1769173014924.png',
        'avatar_m04_mont_saint_michel_1769173032318.png',
        'avatar_m05_carcassonne_1769173046066.png',
        'avatar_m06_vieux_port_1769173075671.png',
        'avatar_m07_gare_limoges_1769173090875.png',
        'avatar_m08_strasbourg_1769173105536.png',
        'avatar_m09_grue_jaune_1769173118804.png',
        'avatar_m10_pont_st_nazaire_1769173133350.png',
        'avatar_m11_tour_eiffel_1769173165010.png',
        'avatar_m12_mont_saint_michel_1769173405831.png',
        'avatar_m13_carcassonne_1769173180335.png',
        'avatar_m14_vieux_port_1769173195551.png',
        'avatar_m15_gare_limoges_1769173210281.png',
        'avatar_m16_strasbourg_1769173420841.png',
    ];
    // Themes list removed - using context availableThemes instead

    const handleUpdateProfile = async () => {
        try {
            await api.put('/auth/profile', {
                avatar: selectedAvatar,
                theme: selectedTheme,
            });
            toast.success('Profil mis à jour !');
            window.location.reload();
        } catch (error) {
            toast.error('Mise à jour du profil échouée.');
        }
    };

    return (
        <VintageLayout>
            <div className="min-h-screen text-cream-100 font-sans selection:bg-gold-500/30">
                {/* Navbar - Transparent/Glassmorphism */}
                <nav className="bg-navy-900/50 backdrop-blur-md text-cream-100 border-b border-gold-500/30 sticky top-0 z-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between h-20 items-center">
                            <div className="flex items-center gap-3">
                                <img
                                    src="/assets/logo-text.png"
                                    alt="Réminiscence"
                                    className="h-12 w-auto brightness-0 invert opacity-90 hover:opacity-100 transition-opacity"
                                />
                            </div>
                            <div className="flex items-center gap-6">
                                <div className="hidden md:flex items-center gap-8 mr-4">
                                    <button
                                        onClick={() => {
                                            setActiveTab('OVERVIEW');
                                            audioManager.playSFX('click');
                                        }}
                                        className={`text-sm font-serif font-medium transition-all duration-300 hover:text-gold-300 ${activeTab === 'OVERVIEW' ? 'text-gold-400 border-b-2 border-gold-400' : 'text-cream-200/70'}`}
                                    >
                                        Vue d'ensemble
                                    </button>
                                    <button
                                        onClick={() => {
                                            setActiveTab('ATELIER');
                                            audioManager.playSFX('click');
                                        }}
                                        className={`text-sm font-serif font-medium transition-all duration-300 hover:text-gold-300 ${activeTab === 'ATELIER' ? 'text-gold-400 border-b-2 border-gold-400' : 'text-cream-200/70'}`}
                                    >
                                        Pack Personnalisé
                                    </button>
                                    <button
                                        onClick={() => {
                                            setActiveTab('SETTINGS');
                                            audioManager.playSFX('click');
                                        }}
                                        className={`text-sm font-serif font-medium transition-all duration-300 hover:text-gold-300 ${activeTab === 'SETTINGS' ? 'text-gold-400 border-b-2 border-gold-400' : 'text-cream-200/70'}`}
                                    >
                                        Personnalisation
                                    </button>
                                    <button
                                        onClick={() => {
                                            setActiveTab('HISTORY');
                                            audioManager.playSFX('click');
                                        }}
                                        className={`text-sm font-serif font-medium transition-all duration-300 hover:text-gold-300 ${activeTab === 'HISTORY' ? 'text-gold-400 border-b-2 border-gold-400' : 'text-cream-200/70'}`}
                                    >
                                        Historique
                                    </button>
                                    <button
                                        onClick={() => {
                                            setActiveTab('LEADERBOARD');
                                            audioManager.playSFX('click');
                                        }}
                                        className={`text-sm font-serif font-medium transition-all duration-300 hover:text-gold-300 ${activeTab === 'LEADERBOARD' ? 'text-gold-400 border-b-2 border-gold-400' : 'text-cream-200/70'}`}
                                    >
                                        Classement
                                    </button>
                                </div>

                                <div className="flex items-center gap-4 pl-6 border-l border-gold-500/20">
                                    <div className="text-right hidden sm:block">
                                        <div className="text-sm font-serif font-bold text-gold-100">{user?.username}</div>
                                        <div className="text-xs text-gold-400/80 capitalize tracking-wider">{user?.role.toLowerCase()}</div>
                                    </div>
                                    <div className="w-10 h-10 rounded-full bg-navy-800 border border-gold-500 flex items-center justify-center overflow-hidden shadow-lg">
                                        {/* Avatar Display */}
                                        {user?.avatar && user.avatar.endsWith('.png') ? (
                                            <img
                                                src={`/assets/avatars/${user.avatar}`}
                                                alt="Avatar"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-navy-900 flex items-center justify-center text-sm font-serif text-gold-300">
                                                {user?.avatar?.substring(0, 2).toUpperCase() || 'U'}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={logout}
                                        className="p-2 text-cream-200/70 hover:text-red-400 transition-colors"
                                        title="Se déconnecter"
                                    >
                                        <LogOut size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>

                <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">

                    {activeTab === 'OVERVIEW' && (
                        <div className="animate-fade-in-up">
                            {/* Welcome Section */}
                            <div className="mb-10 text-center md:text-left">
                                <h1 className="text-4xl font-serif font-bold text-cream-100 mb-2">
                                    Bon retour, <span className="text-gold-400">{user?.username}</span>
                                </h1>
                                <p className="text-cream-200/60 font-serif italic text-lg">
                                    "Vos souvenirs sont la clé de l'énigme."
                                </p>
                            </div>

                            {/* Active Games List */}
                            <ActiveGamesList
                                games={activeGames}
                                onAbandon={handleAbandonGame}
                            />

                            {/* Game History */}
                            {gameHistory.length > 0 && (
                                <div className="mb-10">
                                    <h2 className="text-2xl font-serif font-bold text-cream-100 mb-4 flex items-center gap-3">
                                        <span className="w-8 h-0.5 bg-gold-500 inline-block"></span>
                                        Historique des Parties
                                    </h2>
                                    <GameHistory
                                        history={gameHistory}
                                        currentUserId={user?._id || ''}
                                    />
                                </div>
                            )}

                            {/* Stats Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
                                <div
                                    onClick={() => {
                                        if (navigator.vibrate) navigator.vibrate(50);
                                    }}
                                    className="bg-navy-900/60 backdrop-blur-sm border border-gold-500/30 p-5 md:p-6 rounded-xl flex items-center gap-4 group active:scale-95 transition-all duration-200 shadow-lg touch-manipulation cursor-pointer"
                                >
                                    <div className="p-3 md:p-4 bg-gold-500/10 rounded-full text-gold-400 border border-gold-500/30 group-hover:scale-110 transition-transform">
                                        <Trophy size={24} className="md:w-7 md:h-7" />
                                    </div>
                                    <div>
                                        <div className="text-2xl md:text-3xl font-serif font-bold text-cream-100">{user?.stats?.wins || 0}</div>
                                        <div className="text-xs md:text-sm font-medium text-gold-200/70 uppercase tracking-wide">Victoires</div>
                                    </div>
                                </div>

                                <div
                                    onClick={() => {
                                        if (navigator.vibrate) navigator.vibrate(50);
                                    }}
                                    className="bg-navy-900/60 backdrop-blur-sm border border-gold-500/30 p-5 md:p-6 rounded-xl flex items-center gap-4 group active:scale-95 transition-all duration-200 shadow-lg touch-manipulation cursor-pointer"
                                >
                                    <div className="p-3 md:p-4 bg-blue-500/10 rounded-full text-blue-400 border border-blue-500/30 group-hover:scale-110 transition-transform">
                                        <Gamepad2 size={24} className="md:w-7 md:h-7" />
                                    </div>
                                    <div>
                                        <div className="text-2xl md:text-3xl font-serif font-bold text-cream-100">{user?.stats?.gamesPlayed || 0}</div>
                                        <div className="text-xs md:text-sm font-medium text-gold-200/70 uppercase tracking-wide">Parties jouées</div>
                                    </div>
                                </div>

                                <div
                                    onClick={() => {
                                        if (navigator.vibrate) navigator.vibrate(50);
                                    }}
                                    className="bg-navy-900/60 backdrop-blur-sm border border-gold-500/30 p-5 md:p-6 rounded-xl flex items-center gap-4 group active:scale-95 transition-all duration-200 shadow-lg touch-manipulation cursor-pointer"
                                >
                                    <div className="p-3 md:p-4 bg-purple-500/10 rounded-full text-purple-400 border border-purple-500/30 group-hover:scale-110 transition-transform">
                                        <UserIcon size={24} className="md:w-7 md:h-7" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-end mb-1">
                                            <div className="text-2xl md:text-3xl font-serif font-bold text-cream-100">Niveau {user?.level || 1}</div>
                                            <div className="text-[10px] md:text-xs font-mono text-gold-400">
                                                {user?.xp || 0} XP
                                            </div>
                                        </div>

                                        {/* XP Progress Bar */}
                                        <div className="w-full h-2 bg-navy-950 rounded-full overflow-hidden border border-gold-500/20">
                                            <div
                                                className="h-full bg-gradient-to-r from-purple-600 to-gold-500 relative transition-all duration-1000 ease-out"
                                                style={{
                                                    width: (() => {
                                                        const xp = user?.xp || 0;
                                                        const level = user?.level || 1;
                                                        // XP boundaries
                                                        const currentLevelBaseXP = Math.pow(level - 1, 2) * 100;
                                                        const nextLevelXP = Math.pow(level, 2) * 100;
                                                        const levelDuration = nextLevelXP - currentLevelBaseXP;
                                                        const progress = xp - currentLevelBaseXP;

                                                        // Clamping and percentage
                                                        const percent = Math.min(100, Math.max(0, (progress / levelDuration) * 100));
                                                        return `${percent}%`;
                                                    })()
                                                }}
                                            >
                                                {/* Shimmer effect */}
                                                <div className="absolute inset-0 bg-white/20 w-full animate-[shimmer_2s_infinite]"></div>
                                            </div>
                                        </div>
                                        <div className="text-[10px] md:text-xs font-medium text-gold-200/70 uppercase tracking-wide mt-1 flex justify-between">
                                            <span>Novice</span>
                                            <span>Suivant: {Math.pow((user?.level || 1), 2) * 100} XP</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="relative overflow-hidden group rounded-xl border border-gold-500/50 bg-navy-900/80 shadow-[0_0_30px_rgba(212,175,55,0.1)]">
                                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
                                    <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-gold-500 rounded-full blur-3xl opacity-10 group-hover:opacity-20 transition-opacity"></div>

                                    <div className="relative z-10 p-8 flex flex-col h-full">
                                        <div className="flex justify-between items-start mb-4">
                                            <h2 className="text-3xl font-serif font-bold text-gold-400">Jouer</h2>
                                            <button
                                                onClick={() => navigate('/tutorial')}
                                                className="text-xs font-bold uppercase tracking-wider bg-gold-500/10 text-gold-400 px-3 py-1 rounded-full border border-gold-500/30 hover:bg-gold-500 hover:text-navy-900 transition-colors"
                                            >
                                                Tutoriel
                                            </button>
                                        </div>

                                        <div className="flex-grow space-y-4 mb-6">
                                            <div>
                                                <label className="block text-sm font-serif font-medium text-cream-200/80 mb-1">Pack de Cartes</label>
                                                <select
                                                    value={selectedPackId}
                                                    onChange={(e) => setSelectedPackId(e.target.value)}
                                                    className="w-full bg-navy-950/80 border border-gold-500/30 rounded-lg px-3 py-2 text-cream-100 focus:border-gold-500 outline-none cursor-pointer text-sm"
                                                >
                                                    <optgroup label="Réminiscence" className="bg-navy-900 text-gold-500 font-bold">
                                                        {allPacks.officials.map(pack => (
                                                            <option key={pack._id} value={pack._id} className="bg-navy-900 text-gold-100 font-normal">
                                                                {pack.name} {pack.isAdult ? '(18+)' : ''}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                    {user && allPacks.mine.length > 0 && (
                                                        <optgroup label="Mes Créations" className="bg-navy-900 text-gold-500 font-bold">
                                                            {allPacks.mine.map(pack => (
                                                                <option key={pack._id} value={pack._id} className="bg-navy-900 text-gold-100 font-normal">
                                                                    {pack.name} {pack.isAdult ? '(18+)' : ''}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    )}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-serif font-medium text-cream-200/80 mb-1">Rejoindre via Code (Optionnel)</label>
                                                <input
                                                    type="text"
                                                    value={joinRoomCode}
                                                    onChange={(e) => setJoinRoomCode(e.target.value.toUpperCase())}
                                                    placeholder="Laissez vide pour créer"
                                                    className="w-full bg-navy-950/80 border border-gold-500/30 rounded-lg px-3 py-2 text-cream-100 focus:border-gold-500 outline-none uppercase text-sm placeholder-cream-200/30"
                                                    maxLength={6}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => navigate('/game', { state: { autoStart: true, quickPlay: true, packId: selectedPackId, roomCode: joinRoomCode } })}
                                            className="btn-vintage w-full py-4 text-lg font-bold flex items-center justify-center gap-2"
                                        >
                                            <Gamepad2 size={24} />
                                            <span>Entrer dans l'Arène</span>
                                        </button>
                                    </div>
                                </div>

                                {user?.role === 'ADMIN' && (
                                    <div className="flex flex-col justify-center items-center text-center p-8 border border-gold-500/20 bg-navy-900/40 rounded-xl hover:bg-navy-900/60 transition-colors backdrop-blur-sm">
                                        <div className="w-16 h-16 bg-navy-800 rounded-full flex items-center justify-center text-gold-400 mb-4 border border-gold-500/30 shadow-lg">
                                            <UserIcon size={32} />
                                        </div>
                                        <h2 className="text-2xl font-serif font-bold text-cream-100 mb-3">Administration</h2>
                                        <p className="text-cream-200/60 mb-6 max-w-xs">
                                            Accédez au panneau de contrôle pour gérer le jeu et les joueurs.
                                        </p>
                                        <button
                                            onClick={() => navigate('/admin')}
                                            className="px-6 py-2 border border-gold-500/50 text-gold-400 rounded-lg font-bold hover:bg-gold-500 hover:text-navy-900 transition-all duration-300"
                                        >
                                            Accéder au Bureau
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'ATELIER' && (
                        <div className="animate-fade-in-up">
                            <div className="flex justify-between items-end mb-8 border-b border-gold-500/50 pb-4">
                                <div>
                                    <h2 className="text-3xl font-serif font-bold text-cream-100 mb-2">
                                        Pack Personnalisé
                                    </h2>
                                    <p className="text-cream-200/60">Créez vos propres packs de jeu et partagez-les avec vos amis.</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-gold-400 uppercase tracking-wider mb-1">Quota</div>
                                    <div className="text-2xl font-serif font-bold text-cream-100">
                                        {myPacks.length} <span className="text-cream-200/40">/</span> 10
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* Create New Pack */}
                                <div className="lg:col-span-1">
                                    <div className="bg-navy-900/60 backdrop-blur-md border border-gold-500/30 rounded-xl p-6 shadow-lg">
                                        <h3 className="text-xl font-serif font-bold text-gold-400 mb-6">Nouveau Pack</h3>
                                        <form onSubmit={handleCreatePack} className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-bold text-cream-200 mb-2">Nom du Pack</label>
                                                <input
                                                    type="text"
                                                    value={newPackData.name}
                                                    onChange={e => setNewPackData({ ...newPackData, name: e.target.value })}
                                                    className="w-full bg-navy-950 border border-navy-700 rounded-lg px-4 py-2 text-cream-100 focus:border-gold-500 outline-none"
                                                    placeholder="Ex: Soirée Poker"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-cream-200 mb-2">Description</label>
                                                <textarea
                                                    value={newPackData.description}
                                                    onChange={e => setNewPackData({ ...newPackData, description: e.target.value })}
                                                    className="w-full bg-navy-950 border border-navy-700 rounded-lg px-4 py-2 text-cream-100 focus:border-gold-500 outline-none h-24"
                                                    placeholder="Une courte description..."
                                                    required
                                                />
                                            </div>
                                            <label className="flex items-center gap-3 cursor-pointer group">
                                                <input
                                                    type="checkbox"
                                                    checked={newPackData.isAdult}
                                                    onChange={e => setNewPackData({ ...newPackData, isAdult: e.target.checked })}
                                                    className="w-5 h-5 rounded border-navy-600 bg-navy-950 text-gold-500 focus:ring-gold-500/50"
                                                />
                                                <span className="text-sm text-cream-200 group-hover:text-gold-400 transition-colors">Contenu Adulte (18+)</span>
                                            </label>
                                            <button
                                                type="submit"
                                                disabled={myPacks.length >= 10}
                                                className="w-full btn-vintage py-3 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {myPacks.length >= 10 ? 'Limite atteinte' : 'Créer le Pack'}
                                            </button>
                                        </form>
                                    </div>
                                </div>

                                {/* My Packs List */}
                                <div className="lg:col-span-2">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {myPacks.map(pack => (
                                            <div
                                                key={pack._id}
                                                onClick={() => navigate(`/pack-editor/${pack._id}`)}
                                                className="bg-navy-900/40 border border-gold-500/20 rounded-xl p-4 hover:border-gold-500/50 transition-all group relative overflow-hidden cursor-pointer hover:bg-navy-900/60"
                                            >
                                                <div className="absolute top-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Edit2 size={16} className="text-gold-400" />
                                                </div>
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="font-bold text-lg text-cream-100">{pack.name}</h4>
                                                    {pack.isAdult && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded border border-red-500/30">18+</span>}
                                                </div>
                                                <p className="text-sm text-cream-200/60 line-clamp-2 mb-4">{pack.description}</p>
                                                <div className="flex justify-between items-center text-xs text-gold-500/50">
                                                    <span>{new Date(pack.createdAt).toLocaleDateString()}</span>
                                                    <span className="bg-gold-500/10 px-2 py-1 rounded text-gold-400">Privé</span>
                                                    <span className="text-sm font-semibold text-cream-200/80">
                                                        {pack.cardCount !== undefined ? pack.cardCount : 0} cartes
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                        {myPacks.length === 0 && (
                                            <div className="col-span-full flex flex-col items-center justify-center py-12 text-cream-200/40 border-2 border-dashed border-navy-800 rounded-xl">
                                                <Gamepad2 size={48} className="mb-4 opacity-50" />
                                                <p>Vous n'avez pas encore créé de pack.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'SETTINGS' && (
                        <div className="max-w-3xl mx-auto animate-fade-in-up">
                            <h2 className="text-3xl font-serif font-bold text-cream-100 mb-8 border-b border-gold-500/50 pb-4 inline-block">
                                Personnalisation du Profil
                            </h2>

                            <div className="bg-navy-900/60 backdrop-blur-md border border-gold-500/30 rounded-xl p-8 space-y-10 shadow-xl">

                                {/* Audio Settings */}
                                <div>
                                    <label className="block text-lg font-serif font-bold text-gold-200 mb-6 flex items-center gap-3">
                                        <span className="w-8 h-0.5 bg-gold-500 inline-block"></span>
                                        Ambiance Sonore
                                    </label>

                                    <div className="space-y-6">
                                        {/* Global Mute */}
                                        <div className="flex items-center justify-between p-4 bg-navy-800/50 rounded-lg border border-gold-500/10">
                                            <div className="flex items-center gap-4">
                                                <div className={`p-3 rounded-full ${isMuted ? 'bg-red-500/20 text-red-400' : 'bg-gold-500/20 text-gold-400'}`}>
                                                    {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-cream-100">Couper le son</div>
                                                    <div className="text-sm text-cream-200/60">Désactiver tous les sons</div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    const newState = audioManager.toggleMute();
                                                    setIsMuted(newState);
                                                    audioManager.playSFX('click');
                                                }}
                                                className={`w-14 h-8 rounded-full transition-colors duration-300 relative ${isMuted ? 'bg-red-500/50' : 'bg-gold-500/20'}`}
                                            >
                                                <div className={`absolute top-1 w-6 h-6 rounded-full bg-cream-100 transition-all duration-300 ${isMuted ? 'left-7' : 'left-1'}`}></div>
                                            </button>
                                        </div>

                                        {/* Music Volume */}
                                        <div>
                                            <div className="flex justify-between mb-2">
                                                <span className="text-cream-200 flex items-center gap-2"><Music size={16} /> Musique</span>
                                                <span className="text-gold-400 font-mono">{Math.round(musicVolume * 100)}%</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="1"
                                                step="0.05"
                                                value={musicVolume}
                                                onChange={(e) => {
                                                    const val = parseFloat(e.target.value);
                                                    setMusicVolume(val);
                                                    audioManager.setMusicVolume(val);
                                                }}
                                                className="w-full accent-gold-500 h-2 bg-navy-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        {/* SFX Volume */}
                                        <div>
                                            <div className="flex justify-between mb-2">
                                                <span className="text-cream-200 flex items-center gap-2"><Gamepad2 size={16} /> Effets Sonores</span>
                                                <span className="text-gold-400 font-mono">{Math.round(sfxVolume * 100)}%</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="1"
                                                step="0.05"
                                                value={sfxVolume}
                                                onChange={(e) => {
                                                    const val = parseFloat(e.target.value);
                                                    setSfxVolume(val);
                                                    audioManager.setSFXVolume(val);
                                                    audioManager.playSFX('click');
                                                }}
                                                className="w-full accent-gold-500 h-2 bg-navy-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-lg font-serif font-bold text-gold-200 mb-6 flex items-center gap-3">
                                        <span className="w-8 h-0.5 bg-gold-500 inline-block"></span>
                                        Choisir une Identité
                                    </label>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                                        {avatars.map((av) => (
                                            <button
                                                key={av}
                                                onClick={() => setSelectedAvatar(av)}
                                                className={`aspect-square rounded-xl border-2 overflow-hidden transition-all duration-300 transform hover:scale-105 ${selectedAvatar === av
                                                    ? 'border-gold-500 shadow-[0_0_20px_rgba(212,175,55,0.4)] scale-105 ring-2 ring-gold-500/30'
                                                    : 'border-gold-500/20 hover:border-gold-500/50'
                                                    }`}
                                            >
                                                <img
                                                    src={`/assets/avatars/${av}`}
                                                    alt="Avatar"
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Theme Selection */}
                                <div>
                                    <label className="block text-lg font-serif font-bold text-gold-200 mb-6 flex items-center gap-3">
                                        <span className="w-8 h-0.5 bg-gold-500 inline-block"></span>
                                        Ambiance Visuelle
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {availableThemes.map((t) => (
                                            <button
                                                key={t._id}
                                                onClick={() => {
                                                    setSelectedTheme(t._id);
                                                    setContextTheme(t._id);
                                                }}
                                                className={`p-4 rounded-xl border flex items-center gap-4 transition-all duration-300 ${selectedTheme === t._id
                                                    ? 'border-gold-500 bg-navy-800 text-cream-100 shadow-lg'
                                                    : 'border-gold-500/20 hover:border-gold-500/50 bg-navy-900/50 text-cream-200/70'
                                                    }`}
                                            >
                                                {/* Preview Circle */}
                                                <div
                                                    className="w-12 h-12 rounded-full border-2 border-white/10 shadow-inner"
                                                    style={{ backgroundColor: t.cssVariables['--bg-primary'] || '#000' }}
                                                ></div>
                                                <div className="text-left">
                                                    <div className="font-bold font-serif text-lg">{t.name}</div>
                                                    <div className="text-xs opacity-70 uppercase tracking-wider">Thème</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="pt-8 border-t border-gold-500/20 flex justify-end">
                                    <button
                                        onClick={handleUpdateProfile}
                                        className="btn-vintage px-8 py-3 flex items-center gap-2"
                                    >
                                        <span>Enregistrer les Modifications</span>
                                    </button>
                                </div>

                            </div>
                        </div>
                    )}

                    {/* Content: HISTORY */}
                    {activeTab === 'HISTORY' && (
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 animate-fade-in">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-3 bg-navy-800 rounded-xl border border-gold-500/30">
                                    <HistoryIcon className="text-gold-400" size={32} />
                                </div>
                                <div>
                                    <h2 className="text-3xl font-serif font-bold text-gold-100">Historique des Parties</h2>
                                    <p className="text-cream-200/60 mt-1">Vos souvenirs de batailles passées et vos statistiques.</p>
                                </div>
                            </div>

                            {loadingHistory ? (
                                <div className="text-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500 mx-auto mb-4"></div>
                                    <p className="text-gold-300/80">Chargement des archives...</p>
                                </div>
                            ) : (
                                <GameHistory history={gameHistory} currentUserId={user?._id || ''} />
                            )}
                        </div>
                    )}

                    {/* Content: LEADERBOARD */}
                    {activeTab === 'LEADERBOARD' && (
                        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 animate-fade-in">
                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-serif font-bold text-gold-100 flex items-center justify-center gap-3">
                                    <Trophy className="text-gold-400" />
                                    <span>Panthéon des Souvenirs</span>
                                    <Trophy className="text-gold-400" />
                                </h2>
                                <p className="text-cream-200/60 mt-1">Les esprits les plus lucides de Réminiscence.</p>
                            </div>

                            {loadingLeaderboard ? (
                                <div className="text-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500 mx-auto mb-4"></div>
                                    <p className="text-gold-300/80">Recherche des meilleurs joueurs...</p>
                                </div>
                            ) : (
                                <Leaderboard entries={leaderboard} currentUserId={user?._id || ''} />
                            )}
                        </div>
                    )}

                </main>
            </div >
        </VintageLayout >
    );
};
